import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';

// AWS Pricing Reference Data (US-East-1 Baselines)
const REGIONS = [
  { id: 'us-east-1', name: 'US East (N. Virginia)', multiplier: 1.0 },
  { id: 'us-west-2', name: 'US West (Oregon)', multiplier: 1.0 },
  { id: 'eu-west-1', name: 'EU (Ireland)', multiplier: 1.10 },
  { id: 'ap-northeast-1', name: 'Asia Pacific (Tokyo)', multiplier: 1.20 },
  { id: 'ap-southeast-2', name: 'Asia Pacific (Sydney)', multiplier: 1.25 }
];

const RDS_INSTANCES = [
  { id: 'db.t4g.micro', name: 'db.t4g.micro (2 vCPU, 1 GB RAM)', rate: 0.016, type: 'burstable' },
  { id: 'db.t4g.small', name: 'db.t4g.small (2 vCPU, 2 GB RAM)', rate: 0.032, type: 'burstable' },
  { id: 'db.t4g.medium', name: 'db.t4g.medium (2 vCPU, 4 GB RAM)', rate: 0.064, type: 'burstable' },
  { id: 'db.t3.micro', name: 'db.t3.micro (2 vCPU, 1 GB RAM)', rate: 0.017, type: 'burstable' },
  { id: 'db.t3.small', name: 'db.t3.small (2 vCPU, 2 GB RAM)', rate: 0.034, type: 'burstable' },
  { id: 'db.t3.medium', name: 'db.t3.medium (2 vCPU, 4 GB RAM)', rate: 0.068, type: 'burstable' },
  { id: 'db.m6g.large', name: 'db.m6g.large (2 vCPU, 8 GB RAM)', rate: 0.120, type: 'general' },
  { id: 'db.m6g.xlarge', name: 'db.m6g.xlarge (4 vCPU, 16 GB RAM)', rate: 0.240, type: 'general' },
  { id: 'db.m6g.2xlarge', name: 'db.m6g.2xlarge (8 vCPU, 32 GB RAM)', rate: 0.480, type: 'general' },
  { id: 'db.m7g.large', name: 'db.m7g.large (2 vCPU, 8 GB RAM)', rate: 0.136, type: 'general' },
  { id: 'db.m7g.xlarge', name: 'db.m7g.xlarge (4 vCPU, 16 GB RAM)', rate: 0.272, type: 'general' },
  { id: 'db.m7g.2xlarge', name: 'db.m7g.2xlarge (8 vCPU, 32 GB RAM)', rate: 0.544, type: 'general' },
  { id: 'db.r6g.large', name: 'db.r6g.large (2 vCPU, 16 GB RAM)', rate: 0.180, type: 'memory' },
  { id: 'db.r6g.xlarge', name: 'db.r6g.xlarge (4 vCPU, 32 GB RAM)', rate: 0.360, type: 'memory' },
  { id: 'db.r6g.2xlarge', name: 'db.r6g.2xlarge (8 vCPU, 64 GB RAM)', rate: 0.720, type: 'memory' },
  { id: 'db.r7g.large', name: 'db.r7g.large (2 vCPU, 16 GB RAM)', rate: 0.207, type: 'memory' },
  { id: 'db.r7g.xlarge', name: 'db.r7g.xlarge (4 vCPU, 32 GB RAM)', rate: 0.414, type: 'memory' },
  { id: 'db.r7g.2xlarge', name: 'db.r7g.2xlarge (8 vCPU, 64 GB RAM)', rate: 0.828, type: 'memory' }
];

const AURORA_INSTANCES = [
  { id: 'db.r6g.large', name: 'db.r6g.large (2 vCPU, 16 GB RAM)', rate: 0.200 },
  { id: 'db.r6g.xlarge', name: 'db.r6g.xlarge (4 vCPU, 32 GB RAM)', rate: 0.400 },
  { id: 'db.r6g.2xlarge', name: 'db.r6g.2xlarge (8 vCPU, 64 GB RAM)', rate: 0.800 },
  { id: 'db.r7g.large', name: 'db.r7g.large (2 vCPU, 16 GB RAM)', rate: 0.230 },
  { id: 'db.r7g.xlarge', name: 'db.r7g.xlarge (4 vCPU, 32 GB RAM)', rate: 0.460 },
  { id: 'db.r7g.2xlarge', name: 'db.r7g.2xlarge (8 vCPU, 64 GB RAM)', rate: 0.920 }
];

export default function AwsCostCalculator() {
  const navigate = useNavigate();
  
  // State variables (pre-populated with 1000 users / db.t3.micro / 20GB GP3 defaults)
  const [engine, setEngine] = useState('rds'); // rds | aurora_provisioned | aurora_serverless
  const [region, setRegion] = useState('us-east-1');
  const [deployment, setDeployment] = useState('single-az'); // single-az | multi-az
  const [instanceId, setInstanceId] = useState('db.t3.micro');
  const [storageType, setStorageType] = useState('gp3'); // gp2 | gp3 | io1 (or standard | io_optimized for aurora)
  const [storageGb, setStorageGb] = useState(20);
  
  // Custom IOPS & Throughput config (gp3/io1)
  const [customIopsEnabled, setCustomIopsEnabled] = useState(false);
  const [iops, setIops] = useState(3000);
  const [throughput, setThroughput] = useState(125);
  
  // Serverless configurations
  const [minAcu, setMinAcu] = useState(0.5);
  const [maxAcu, setMaxAcu] = useState(16);
  const [avgAcuUtil, setAvgAcuUtil] = useState(20); // 20% average utilization
  
  // Aurora Standard IO requests
  const [ioRequestsMillion, setIoRequestsMillion] = useState(5); // 5 Million requests
  
  // Data Transfer & Backups
  const [egressGb, setEgressGb] = useState(5);
  const [backupGb, setBackupGb] = useState(20);
  
  // Purchasing Option
  const [purchasingOption, setPurchasingOption] = useState('on_demand'); // on_demand, ri_1yr_no, ri_1yr_all, ri_3yr_no, ri_3yr_all
  
  // Free Tier toggle
  const [freeTierEnabled, setFreeTierEnabled] = useState(false);

  // Result Breakdown State
  const [costBreakdown, setCostBreakdown] = useState({
    compute: 0,
    storage: 0,
    iops: 0,
    transfer: 0,
    backup: 0,
    total: 0
  });

  const [advice, setAdvice] = useState([]);

  // Trigger calculation updates when configurations change
  useEffect(() => {
    calculateCosts();
  }, [engine, region, deployment, instanceId, storageType, storageGb, customIopsEnabled, iops, throughput, minAcu, maxAcu, avgAcuUtil, ioRequestsMillion, egressGb, backupGb, purchasingOption, freeTierEnabled]);

  const calculateCosts = () => {
    const selectedRegion = REGIONS.find(r => r.id === region);
    const multiplier = selectedRegion ? selectedRegion.multiplier : 1.0;
    
    // --- 1. Compute Cost ---
    let computeCost = 0;
    let baselineComputeRate = 0;
    let isReservedApplicable = false;

    if (engine === 'rds') {
      const inst = RDS_INSTANCES.find(i => i.id === instanceId) || RDS_INSTANCES[3];
      baselineComputeRate = inst.rate;
      isReservedApplicable = true;
    } else if (engine === 'aurora_provisioned') {
      const inst = AURORA_INSTANCES.find(i => i.id === instanceId) || AURORA_INSTANCES[0];
      baselineComputeRate = inst.rate;
      isReservedApplicable = true;
    }

    if (engine === 'aurora_serverless') {
      // Serverless ACU Calculation
      // Average ACUs = min + (max - min) * (avg % / 100)
      const avgAcu = minAcu + (maxAcu - minAcu) * (avgAcuUtil / 100);
      const acuHourRate = 0.12 * multiplier;
      computeCost = avgAcu * acuHourRate * 730; // 730 hours/month average
    } else {
      // Provisioned RDS/Aurora
      let rate = baselineComputeRate * multiplier;
      
      // Multi-AZ double compute cost
      if (deployment === 'multi-az') {
        rate = rate * 2;
      }

      // Apply Reserved Instance Discounts (applied only to compute)
      if (isReservedApplicable) {
        if (purchasingOption === 'ri_1yr_no') rate *= 0.70;      // 30% discount
        else if (purchasingOption === 'ri_1yr_all') rate *= 0.65; // 35% discount
        else if (purchasingOption === 'ri_3yr_no') rate *= 0.50;  // 50% discount
        else if (purchasingOption === 'ri_3yr_all') rate *= 0.45; // 55% discount
      }

      computeCost = rate * 730;

      // Free Tier Compute Check (RDS single-az micro for 1st year free)
      if (freeTierEnabled && engine === 'rds' && deployment === 'single-az' && 
          (instanceId === 'db.t3.micro' || instanceId === 'db.t2.micro' || instanceId === 'db.t4g.micro')) {
        computeCost = 0;
      }
    }

    // --- 2. Storage Cost ---
    let storageCost = 0;
    let iopsCost = 0;

    if (engine === 'rds') {
      if (storageType === 'gp2') {
        let baseRate = 0.115 * multiplier;
        if (deployment === 'multi-az') baseRate *= 2;
        storageCost = storageGb * baseRate;

        if (freeTierEnabled && storageGb <= 20) {
          storageCost = 0;
        }
      } else if (storageType === 'gp3') {
        let baseRate = 0.115 * multiplier;
        if (deployment === 'multi-az') baseRate *= 2;
        storageCost = storageGb * baseRate;

        // Custom IOPS & Throughput on GP3
        if (customIopsEnabled) {
          const extraIops = Math.max(0, iops - 3000);
          const extraThroughput = Math.max(0, throughput - 125);
          iopsCost = (extraIops * 0.005 + extraThroughput * 0.04) * (deployment === 'multi-az' ? 2 : 1) * multiplier;
        }

        if (freeTierEnabled && storageGb <= 20) {
          storageCost = 0;
          if (!customIopsEnabled) iopsCost = 0;
        }
      } else if (storageType === 'io1') {
        let baseRate = 0.125 * multiplier;
        if (deployment === 'multi-az') baseRate *= 2;
        storageCost = storageGb * baseRate;

        const ioRate = 0.10 * multiplier;
        iopsCost = iops * ioRate * (deployment === 'multi-az' ? 2 : 1);
      }
    } else {
      // Aurora Storage
      if (storageType === 'standard') {
        storageCost = storageGb * 0.10 * multiplier;
        iopsCost = ioRequestsMillion * 0.20 * multiplier; // $0.20 per million requests
      } else {
        // I/O-Optimized
        storageCost = storageGb * 0.22 * multiplier;
        iopsCost = 0; // No I/O fee
      }
    }

    // --- 3. Backup Storage Cost ---
    // Backup up to 100% of database size is free. Additional is $0.095/GB
    let backupCost = 0;
    const freeBackupLimit = freeTierEnabled ? Math.max(20, storageGb) : storageGb;
    if (backupGb > freeBackupLimit) {
      backupCost = (backupGb - freeBackupLimit) * 0.095 * multiplier;
    }

    // --- 4. Data Transfer Cost ---
    let transferCost = 0;
    let billableEgress = egressGb;
    if (freeTierEnabled) {
      billableEgress = Math.max(0, egressGb - 100); // 100 GB free egress
    }
    transferCost = billableEgress * 0.09; // $0.09 per GB egress standard

    // --- Totals ---
    const total = computeCost + storageCost + iopsCost + backupCost + transferCost;

    setCostBreakdown({
      compute: parseFloat(computeCost.toFixed(2)),
      storage: parseFloat(storageCost.toFixed(2)),
      iops: parseFloat(iopsCost.toFixed(2)),
      backup: parseFloat(backupCost.toFixed(2)),
      transfer: parseFloat(transferCost.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    });

    // --- Generate Real-time Optimization Advice ---
    const listAdvice = [];
    
    if (engine === 'rds' && storageType === 'gp2') {
      listAdvice.push({
        type: 'warning',
        text: `💡 Switch GP2 to GP3 storage: You are currently using GP2. Switching to GP3 keeps the same baseline performance but saves storage costs and gives you 3,000 IOPS free.`
      });
    }

    if (purchasingOption === 'on_demand' && engine !== 'aurora_serverless') {
      const ri1YrSave = computeCost * 0.30;
      const ri3YrSave = computeCost * 0.50;
      listAdvice.push({
        type: 'success',
        text: `🔑 Reserved Instances Savings: Switching this database to a 1-Year Reserved contract would save you ~$${ri1YrSave.toFixed(2)}/month. A 3-Year contract would save ~$${ri3YrSave.toFixed(2)}/month.`
      });
    }

    if (engine === 'aurora_serverless' && maxAcu > 32) {
      listAdvice.push({
        type: 'info',
        text: `⚠️ High Serverless Limit: Your max ACU limit is high (${maxAcu} ACUs = 64GB RAM). Set a lower Max ACU limit (e.g. 8 or 16) if you want to prevent unexpected cost spikes during traffic anomalies.`
      });
    }

    if (engine === 'rds' && storageType === 'io1' && iops < 3000) {
      listAdvice.push({
        type: 'warning',
        text: `💡 Avoid IO1 for low IOPS: You provisioned ${iops} IOPS on IO1. You can achieve this with GP3 storage for a fraction of the cost, saving ~$${(iopsCost).toFixed(2)}/month.`
      });
    }

    if (backupGb > storageGb) {
      listAdvice.push({
        type: 'info',
        text: `📦 Optimize backup retention: You have ${backupGb} GB backups for a ${storageGb} GB database. Shortening your snapshot retention period can reduce backup fees.`
      });
    }

    setAdvice(listAdvice);
  };

  // Export to PDF using jsPDF
  const handleExportPdf = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Background Styling
    doc.setFillColor(64, 23, 4); // Navy primary brand color #401704
    doc.rect(0, 0, 210, 40, 'F');

    // Header Text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('AWS PostgreSQL Monthly Cost Estimate', 14, 18);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}  |  New Sowtha Store Billing Software`, 14, 28);

    doc.setTextColor(52, 58, 64);
    
    // Config details section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('1. Database Configuration Details', 14, 55);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    let engineLabel = 'Amazon RDS for PostgreSQL';
    if (engine === 'aurora_provisioned') engineLabel = 'Amazon Aurora PostgreSQL (Provisioned)';
    if (engine === 'aurora_serverless') engineLabel = 'Amazon Aurora PostgreSQL (Serverless v2)';

    const selectedRegion = REGIONS.find(r => r.id === region);

    const configLines = [
      `Database Engine: ${engineLabel}`,
      `AWS Region: ${selectedRegion ? selectedRegion.name : region}`,
      `Deployment Type: ${deployment === 'single-az' ? 'Single-AZ' : 'Multi-AZ (Primary + Standby)'}`,
      engine !== 'aurora_serverless' ? `Database Instance Class: ${instanceId}` : `ACU Range: ${minAcu} - ${maxAcu} ACUs (Avg Util: ${avgAcuUtil}%)`,
      `Storage Configuration: ${storageGb} GB (${storageType.toUpperCase()})`,
      engine === 'rds' && customIopsEnabled ? `Provisioned IOPS/Throughput: ${iops} IOPS, ${throughput} MB/s` : '',
      `Automated Backups Size: ${backupGb} GB`,
      `Monthly Internet Egress: ${egressGb} GB`,
      `Purchasing Option: ${purchasingOption.toUpperCase().replace('_', ' ')}`,
      `AWS 12-Month Free Tier Simulated: ${freeTierEnabled ? 'YES' : 'NO'}`
    ].filter(Boolean);

    let y = 65;
    configLines.forEach(line => {
      doc.text(`• ${line}`, 18, y);
      y += 6;
    });

    // Cost Breakdown Section
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('2. Monthly Cost Breakdown', 14, y);
    
    y += 10;
    // Draw table headers
    doc.setFillColor(233, 236, 239);
    doc.rect(14, y - 6, 182, 8, 'F');
    doc.setFontSize(10);
    doc.text('Cost Element', 18, y - 1);
    doc.text('Calculated Monthly Cost (USD)', 130, y - 1);
    
    const tableData = [
      { name: 'Compute Charges (Instance / ACU)', value: `$${costBreakdown.compute.toFixed(2)}` },
      { name: 'Provisioned Database Storage', value: `$${costBreakdown.storage.toFixed(2)}` },
      { name: 'Provisioned IOPS & Throughput', value: `$${costBreakdown.iops.toFixed(2)}` },
      { name: 'Automated Backups Over Allocation', value: `$${costBreakdown.backup.toFixed(2)}` },
      { name: 'Outbound Data Transfer (Internet Egress)', value: `$${costBreakdown.transfer.toFixed(2)}` }
    ];

    doc.setFont('helvetica', 'normal');
    tableData.forEach(row => {
      y += 8;
      doc.text(row.name, 18, y - 1);
      doc.text(row.value, 130, y - 1);
      doc.line(14, y, 196, y); // Row line
    });

    // Grand Total Row
    y += 10;
    doc.setFillColor(253, 246, 227); // Gold pale #fdf6e3
    doc.rect(14, y - 6, 182, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('ESTIMATED MONTHLY TOTAL:', 18, y);
    doc.setTextColor(201, 168, 76); // Gold #c9a84c
    doc.text(`$${costBreakdown.total.toFixed(2)} / Month`, 130, y);

    // Annual cost info
    doc.setTextColor(108, 117, 125);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'italic');
    doc.text(`Estimated Annual Database Cost: $${(costBreakdown.total * 12).toFixed(2)} / Year`, 18, y + 8);

    // Footer note
    doc.setTextColor(140, 140, 140);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Disclaimer: This is an architectural cost estimation based on standard AWS baseline rates and multipliers.', 14, 280);

    doc.save(`AWS_PostgreSQL_Cost_Estimate_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Helper for computing SVG Pie Chart coordinates
  const getSvgPiePaths = () => {
    const data = [
      { name: 'Compute', val: costBreakdown.compute, color: '#401704' }, // Navy
      { name: 'Storage', val: costBreakdown.storage, color: '#c9a84c' }, // Gold
      { name: 'IOPS', val: costBreakdown.iops, color: '#1d6fa4' },     // Blue
      { name: 'Backup', val: costBreakdown.backup, color: '#d97706' },   // Amber
      { name: 'Transfer', val: costBreakdown.transfer, color: '#2d8a4e' } // Green
    ].filter(d => d.val > 0);

    const totalVal = data.reduce((s, d) => s + d.val, 0) || 1;
    let accumulatedAngle = 0;

    return data.map(slice => {
      const percentage = slice.val / totalVal;
      const angle = percentage * 360;
      
      // Arc coordinates
      const x1 = 100 + 80 * Math.cos((accumulatedAngle - 90) * Math.PI / 180);
      const y1 = 100 + 80 * Math.sin((accumulatedAngle - 90) * Math.PI / 180);
      
      accumulatedAngle += angle;
      
      const x2 = 100 + 80 * Math.cos((accumulatedAngle - 90) * Math.PI / 180);
      const y2 = 100 + 80 * Math.sin((accumulatedAngle - 90) * Math.PI / 180);
      
      const largeArc = angle > 180 ? 1 : 0;
      
      const path = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;
      
      return {
        ...slice,
        path,
        percentage: (percentage * 100).toFixed(1)
      };
    });
  };

  const slices = getSvgPiePaths();

  return (
    <div className="page-body fade-in" style={{ maxWidth: 1200, margin: '0 auto' }}>
      
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, borderBottom: '2px solid var(--gray-200)', paddingBottom: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2.1rem', color: 'var(--navy)', fontWeight: 700 }}>
            AWS PostgreSQL Database Cost Calculator
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: '0.9rem', marginTop: 4 }}>
            Estimate monthly costs for Amazon RDS PostgreSQL and Amazon Aurora Serverless v2/Provisioned.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" onClick={() => navigate('/login')}>
            🚪 Back to Login
          </button>
          <button className="btn btn-gold" onClick={handleExportPdf}>
            📄 Export Estimate PDF
          </button>
        </div>
      </div>

      {/* Calculator Grid */}
      <div className="billing-layout" style={{ gridTemplateColumns: '1.4fr 1fr', gap: 28 }}>
        
        {/* Configurations Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          
          {/* Section: Engine choice */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🔌 1. Select PostgreSQL Engine</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { id: 'rds', label: 'RDS PostgreSQL', desc: 'Managed Standard DB' },
                  { id: 'aurora_provisioned', label: 'Aurora Provisioned', desc: 'Clustered Enterprise DB' },
                  { id: 'aurora_serverless', label: 'Aurora Serverless v2', desc: 'Auto-Scaling DB' }
                ].map(opt => (
                  <button key={opt.id} 
                    style={{
                      padding: '16px',
                      borderRadius: 'var(--radius-md)',
                      border: engine === opt.id ? '2px solid var(--gold)' : '2px solid var(--gray-200)',
                      background: engine === opt.id ? 'var(--gold-pale)' : 'var(--white)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.18s ease'
                    }}
                    onClick={() => {
                      setEngine(opt.id);
                      // Reset properties if engine switches
                      if (opt.id === 'rds') {
                        setStorageType('gp3');
                        setInstanceId('db.t3.micro');
                      } else if (opt.id === 'aurora_provisioned') {
                        setStorageType('standard');
                        setInstanceId('db.r6g.large');
                        setDeployment('single-az');
                      } else {
                        setStorageType('standard');
                        setDeployment('single-az');
                      }
                    }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: '0.92rem' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Section: Deployment & Compute Configurations */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">⚙️ 2. Deployment & Instance Settings</h3>
            </div>
            <div className="card-body">
              <div className="form-row" style={{ marginBottom: 18 }}>
                <div className="form-group">
                  <label className="form-label">AWS Region</label>
                  <select className="form-control" value={region} onChange={e => setRegion(e.target.value)}>
                    {REGIONS.map(r => (
                      <option key={r.id} value={r.id}>{r.name} (x{r.multiplier})</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Deployment Mode</label>
                  {engine === 'aurora_serverless' || engine === 'aurora_provisioned' ? (
                    <div style={{ fontSize: '0.82rem', padding: '10px 14px', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-sm)', background: 'var(--gray-50)', color: 'var(--gray-500)' }}>
                      Multi-AZ (Aurora replication is active by default)
                    </div>
                  ) : (
                    <select className="form-control" value={deployment} onChange={e => setDeployment(e.target.value)}>
                      <option value="single-az">Single-AZ (Development / Production)</option>
                      <option value="multi-az">Multi-AZ (High Availability Standby - 2x Compute)</option>
                    </select>
                  )}
                </div>
              </div>

              {engine !== 'aurora_serverless' ? (
                <div className="form-group">
                  <label className="form-label">Database Instance Class</label>
                  <select className="form-control" value={instanceId} onChange={e => setInstanceId(e.target.value)}>
                    {engine === 'rds' ? (
                      RDS_INSTANCES.map(inst => (
                        <option key={inst.id} value={inst.id}>
                          {inst.name} — ${inst.rate.toFixed(3)}/hr
                        </option>
                      ))
                    ) : (
                      AURORA_INSTANCES.map(inst => (
                        <option key={inst.id} value={inst.id}>
                          {inst.name} — ${inst.rate.toFixed(3)}/hr
                        </option>
                      ))
                    )}
                  </select>
                </div>
              ) : (
                /* Aurora Serverless Range Sliders */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label className="form-label">Min ACU ({minAcu} ACU)</label>
                      <input className="form-control" type="number" min="0.5" max="128" step="0.5" value={minAcu} onChange={e => setMinAcu(Math.max(0.5, parseFloat(e.target.value) || 0.5))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max ACU ({maxAcu} ACU)</label>
                      <input className="form-control" type="number" min="0.5" max="128" step="0.5" value={maxAcu} onChange={e => setMaxAcu(Math.max(minAcu, parseFloat(e.target.value) || minAcu))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Avg Util ({avgAcuUtil}%)</label>
                      <input type="range" min="0" max="100" value={avgAcuUtil} onChange={e => setAvgAcuUtil(parseInt(e.target.value))} style={{ width: '100%', marginTop: 8 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', background: 'var(--gray-100)', padding: '10px 14px', borderRadius: 'var(--radius-sm)' }}>
                    💡 1 ACU provides approximately 2 GB of memory, matching CPU and networking power. Your configuration scales between <strong>{minAcu * 2} GB</strong> and <strong>{maxAcu * 2} GB RAM</strong>.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section: Storage Config */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🗄️ 3. Database Disk Storage</h3>
            </div>
            <div className="card-body">
              <div className="form-row" style={{ marginBottom: 18 }}>
                <div className="form-group">
                  <label className="form-label">Storage Type</label>
                  {engine === 'rds' ? (
                    <select className="form-control" value={storageType} onChange={e => setStorageType(e.target.value)}>
                      <option value="gp3">General Purpose SSD (gp3 - Recommended)</option>
                      <option value="gp2">General Purpose SSD (gp2 - Legacy)</option>
                      <option value="io1">Provisioned IOPS SSD (io1)</option>
                    </select>
                  ) : (
                    <select className="form-control" value={storageType} onChange={e => setStorageType(e.target.value)}>
                      <option value="standard">Aurora Standard (Pay per Storage + I/Os)</option>
                      <option value="io_optimized">Aurora I/O-Optimized (Flat Storage fee, Zero I/O fees)</option>
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Storage Size (GB)</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="range" min="10" max="1000" step="10" value={storageGb} onChange={e => setStorageGb(parseInt(e.target.value))} style={{ flex: 1 }} />
                    <input className="form-control" type="number" min="10" max="10000" value={storageGb} onChange={e => setStorageGb(Math.max(10, parseInt(e.target.value) || 10))} style={{ width: 90 }} />
                  </div>
                </div>
              </div>

              {/* GP3/IO1 settings */}
              {engine === 'rds' && (storageType === 'gp3' || storageType === 'io1') && (
                <div style={{ background: 'var(--gray-50)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px dashed var(--gray-300)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <input type="checkbox" id="custom-iops" checked={customIopsEnabled} onChange={e => setCustomIopsEnabled(e.target.checked)} />
                    <label htmlFor="custom-iops" style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-800)', cursor: 'pointer' }}>
                      Configure Custom IOPS & Throughput
                    </label>
                  </div>
                  {customIopsEnabled && (
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Provisioned IOPS</label>
                        <input className="form-control" type="number" min="1000" max="80000" step="500" value={iops} onChange={e => setIops(Math.max(1000, parseInt(e.target.value) || 1000))} />
                        <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>gp3 includes 3,000 IOPS free.</span>
                      </div>
                      {storageType === 'gp3' && (
                        <div className="form-group">
                          <label className="form-label">Throughput (MB/s)</label>
                          <input className="form-control" type="number" min="125" max="1000" step="25" value={throughput} onChange={e => setThroughput(Math.max(125, parseInt(e.target.value) || 125))} />
                          <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>gp3 includes 125 MB/s free.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Aurora Standard IO request config */}
              {engine !== 'rds' && storageType === 'standard' && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Expected Storage IO Requests (Millions / month)</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="range" min="0" max="100" value={ioRequestsMillion} onChange={e => setIoRequestsMillion(parseInt(e.target.value))} style={{ flex: 1 }} />
                    <input className="form-control" type="number" min="0" value={ioRequestsMillion} onChange={e => setIoRequestsMillion(Math.max(0, parseInt(e.target.value) || 0))} style={{ width: 90 }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section: Backups & Data Transfer */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📡 4. Backups & Network Data Transfer</h3>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Automated Backup Storage (GB)</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="range" min="0" max="500" step="5" value={backupGb} onChange={e => setBackupGb(parseInt(e.target.value))} style={{ flex: 1 }} />
                    <input className="form-control" type="number" min="0" value={backupGb} onChange={e => setBackupGb(Math.max(0, parseInt(e.target.value) || 0))} style={{ width: 90 }} />
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Backup storage up to your database volume size is 100% free.</span>
                </div>

                <div className="form-group">
                  <label className="form-label">Internet Egress Transfer (GB/month)</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="range" min="0" max="500" step="5" value={egressGb} onChange={e => setEgressGb(parseInt(e.target.value))} style={{ flex: 1 }} />
                    <input className="form-control" type="number" min="0" value={egressGb} onChange={e => setEgressGb(Math.max(0, parseInt(e.target.value) || 0))} style={{ width: 90 }} />
                  </div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Data leaving AWS to the internet ($0.09/GB).</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Advanced Purchasing Model */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🏷️ 5. Purchasing Terms & Savings</h3>
            </div>
            <div className="card-body">
              <div className="form-row" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Purchasing Strategy</label>
                  {engine === 'aurora_serverless' ? (
                    <div style={{ fontSize: '0.82rem', padding: '10px 14px', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-sm)', background: 'var(--gray-50)', color: 'var(--gray-500)' }}>
                      On-Demand (Reserved pricing is not directly supported for Serverless compute)
                    </div>
                  ) : (
                    <select className="form-control" value={purchasingOption} onChange={e => setPurchasingOption(e.target.value)}>
                      <option value="on_demand">On-Demand (Flexible hourly rate)</option>
                      <option value="ri_1yr_no">1-Year Reserved Instance (No Upfront - ~30% off)</option>
                      <option value="ri_1yr_all">1-Year Reserved Instance (All Upfront - ~35% off)</option>
                      <option value="ri_3yr_no">3-Year Reserved Instance (No Upfront - ~50% off)</option>
                      <option value="ri_3yr_all">3-Year Reserved Instance (All Upfront - ~55% off)</option>
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>AWS 12-Month Free Tier</span>
                    <span className={`badge ${freeTierEnabled ? 'badge-green' : 'badge-gray'}`} style={{ transform: 'scale(0.85)', transformOrigin: 'right' }}>
                      {freeTierEnabled ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </label>
                  <button 
                    className={`btn ${freeTierEnabled ? 'btn-success' : 'btn-outline'}`} 
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => setFreeTierEnabled(!freeTierEnabled)}
                  >
                    {freeTierEnabled ? '🎁 Simulating Free Tier Discounts' : '🚀 Simulate Free Tier (First Year)'}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Calculations & Cost Breakdown Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: 'sticky', top: 92 }}>
          
          {/* Card: Monthly Cost Total */}
          <div className="card" style={{ background: 'var(--navy)', color: 'var(--white)', border: 'none', boxShadow: 'var(--shadow-lg)' }}>
            <div className="card-body" style={{ padding: '32px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1.2px' }}>
                Estimated Monthly Cost
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.4rem', fontWeight: 700, color: 'var(--gold)', margin: '12px 0 6px' }}>
                ${costBreakdown.total.toFixed(2)}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                Annual Estimate: <strong>${(costBreakdown.total * 12).toFixed(2)} / yr</strong>
              </div>

              {freeTierEnabled && (
                <div style={{ marginTop: 18, background: 'rgba(45,138,78,0.25)', border: '1px solid var(--green)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: '0.78rem', color: '#8ee6aa' }}>
                  🎁 Free Tier discounts have been subtracted. This matches your first-year AWS bill.
                </div>
              )}
            </div>
          </div>

          {/* Card: Cost Breakdown Chart & Legend */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📊 Cost Allocation</h3>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
              
              {costBreakdown.total > 0 ? (
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
                  <svg width="180" height="180" viewBox="0 0 200 200">
                    {slices.map((slice, index) => (
                      <path key={index} d={slice.path} fill={slice.color} style={{ transition: 'all 0.3s ease' }}>
                        <title>{slice.name}: {slice.percentage}%</title>
                      </path>
                    ))}
                    <circle cx="100" cy="100" r="50" fill="var(--white)" />
                  </svg>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                    {[
                      { name: 'Compute', val: costBreakdown.compute, color: '#401704' },
                      { name: 'Storage', val: costBreakdown.storage, color: '#c9a84c' },
                      { name: 'IOPS & IOs', val: costBreakdown.iops, color: '#1d6fa4' },
                      { name: 'Backups', val: costBreakdown.backup, color: '#d97706' },
                      { name: 'Data Transfer', val: costBreakdown.transfer, color: '#2d8a4e' }
                    ].map(legend => (
                      <div key={legend.name} style={{ display: 'flex', justifySelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 12, height: 12, borderRadius: '2px', background: legend.color }} />
                          <span style={{ color: 'var(--gray-700)' }}>{legend.name}</span>
                        </div>
                        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>
                          ${legend.val.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '40px 0', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                  No costs calculated yet.
                </div>
              )}
            </div>
          </div>

          {/* Card: Real-time Advice & Tips */}
          {advice.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">💡 Architecture Optimization Tips</h3>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 220, overflowY: 'auto', padding: '16px 20px' }}>
                {advice.map((item, index) => (
                  <div key={index} style={{
                    fontSize: '0.82rem',
                    lineHeight: '1.4',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-sm)',
                    borderLeft: '4px solid',
                    borderColor: item.type === 'warning' ? 'var(--amber)' : item.type === 'success' ? 'var(--green)' : 'var(--blue)',
                    background: item.type === 'warning' ? 'var(--amber-light)' : item.type === 'success' ? 'var(--green-light)' : 'var(--blue-light)',
                    color: item.type === 'warning' ? 'var(--amber)' : item.type === 'success' ? 'var(--green)' : 'var(--blue)'
                  }}>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

      </div>

    </div>
  );
}
